import { StatCard, type StatCardProps } from './StatCard'

/** One card. The shape is StatCard's, so the two cannot drift. */
export type Kpi = Pick<StatCardProps, 'label' | 'value' | 'caption' | 'delta' | 'icon' | 'tint'>

export interface KpiGridProps {
  items: Kpi[]
  /** Rendered instead of the cards when there is nothing to count yet. */
  emptyNote?: string
}

/**
 * A row of stat cards, laid out the same way everywhere.
 *
 * <p>Every screen that shows figures had written its own
 * `grid gap-4 sm:grid-cols-2 lg:grid-cols-4`, so the columns broke at different widths on
 * different pages and a five-card screen wrapped one card onto a line of its own. The
 * component owns the breakpoints and adjusts them to the number of cards, which is the part
 * nobody was doing by hand.
 */
export function KpiGrid({ items, emptyNote }: KpiGridProps) {
  if (items.length === 0) {
    return emptyNote
      ? <p className="text-sm text-[var(--color-muted)]">{emptyNote}</p>
      : null
  }

  // Three cards on a wide screen should be three across, not three of four columns with a
  // gap where the fourth would be.
  const columns = items.length <= 2 ? 'sm:grid-cols-2'
    : items.length === 3 ? 'sm:grid-cols-2 lg:grid-cols-3'
      : items.length === 5 ? 'sm:grid-cols-2 lg:grid-cols-5'
        : 'sm:grid-cols-2 lg:grid-cols-4'

  return (
    <div className={`grid gap-4 ${columns}`}>
      {items.map((kpi) => (
        <StatCard key={kpi.label} {...kpi} />
      ))}
    </div>
  )
}
